"use client";

import React, { useState } from "react";
import { CompanyReviewCriterias } from "@/modules/types/enum";
import { StarIcon } from "hugeicons-react";
import { Textarea } from "@headlessui/react";
import { useClientTranslations } from "@/lib/translate";
import { businessService } from "@/modules/services/businessService";

const CRITERIA_OPTIONS = Object.entries(CompanyReviewCriterias);

const WriteReviewForm = ({
  companyId,
  onClose,
}: {
  companyId: string;
  onClose?: () => void;
}) => {
  const { t } = useClientTranslations("review");
  const [overallRating, setOverallRating] = useState(0);
  const [review, setReview] = useState("");
  const [criterias, setCriterias] = useState([
    { criteria: "", starRating: 0, review: "" },
  ]);

  const handleCriteriaChange = (idx: number, field: string, value: any) => {
    setCriterias((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c))
    );
  };

  const addCriteria = () => {
    if (criterias.length >= CRITERIA_OPTIONS.length) {
      return;
    }
    setCriterias((prev) => [
      ...prev,
      { criteria: "", starRating: 0, review: "" },
    ]);
  };

  const removeCriteria = (idx: number) => {
    setCriterias((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await businessService.createReview({
        businessId: companyId,
        review,
        starRating: overallRating,
        criterias: criterias.filter((c) => c.criteria !== "") as any,
      });
      if (onClose) onClose();
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form
      className="w-full bg-white p-6 rounded-lg shadow-md max-w-xl mx-auto"
      onSubmit={handleSubmit}
    >
      <h2 className="text-lg font-semibold mb-4 text-primary">
        {t("write_a_review")}
      </h2>
      <div className="mb-4">
        <label className="block font-medium mb-1 text-gray-700">
          {t("overall_rating")}
        </label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              type="button"
              key={star}
              onClick={() => setOverallRating(star)}
              className="focus:outline-none"
            >
              <StarIcon
                className={`w-7 h-7 ${star <= overallRating ? "fill-yellow-400" : "fill-gray-200"
                  }`}
              />
            </button>
          ))}
          <span className="ml-2 text-gray-700">{overallRating}/5</span>
        </div>
      </div>
      <div className="mb-4">
        <label
          htmlFor="review"
          className="block font-medium mb-1 text-gray-700"
        >
          {t("your_review")}
        </label>
        <Textarea
          id="review"
          className="w-full rounded-md p-2 min-h-[60px] border border-gray-200 text-gray-700"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder={t("share_your_overall_experience")}
        />
      </div>
      <div className="mb-4">
        <label
          htmlFor="criteria"
          className="block font-medium mb-2 text-gray-700"
        >
          {t("criteria_reviews")}
        </label>
        {criterias.map((c, idx) => {
          const selectedCriteria = criterias
            .map((item, i) => (i !== idx ? item.criteria : null))
            .filter(Boolean);
          return (
            <div
              id="criteria"
              key={idx}
              className="mb-3 p-3 rounded-lg bg-gray-50 relative"
            >
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <select
                  className="flex-1 min-w-0 rounded-md p-1 text-gray-700 border border-gray-200 text-sm"
                  value={c.criteria}
                  onChange={(e) =>
                    handleCriteriaChange(idx, "criteria", e.target.value)
                  }
                  required
                >
                  <option value="">{t("select_criteria")}</option>
                  {CRITERIA_OPTIONS.map(([key, value]) => (
                    <option
                      key={key}
                      value={value}
                      disabled={selectedCriteria.includes(value)}
                    >
                      {t(value)}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      onClick={() =>
                        handleCriteriaChange(idx, "starRating", star)
                      }
                      className="focus:outline-none"
                    >
                      <StarIcon
                        className={`w-5 h-5 ${
                          star <= c.starRating
                            ? "fill-yellow-400"
                            : "fill-gray-200"
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-1 text-xs text-gray-700">
                    {c.starRating}/5
                  </span>
                </div>
                {criterias.length > 1 && (
                  <button
                    type="button"
                    className="text-red-500 text-xs whitespace-nowrap"
                    onClick={() => removeCriteria(idx)}
                  >
                    {t("remove_criteria")}
                  </button>
                )}
              </div>
              <Textarea
                className="w-full rounded-md p-2 min-h-[40px] border border-gray-200 text-gray-700 text-sm"
                value={c.review}
                onChange={(e) =>
                  handleCriteriaChange(idx, "review", e.target.value)
                }
                placeholder={t("short_review_for_this_criteria")}
              />
            </div>
          );
        })}
        {criterias.length < CRITERIA_OPTIONS.length && (
          <button
            type="button"
            className="mt-2 px-3 py-1 border border-primary text-primary rounded-md"
            onClick={addCriteria}
          >
            + {t("add_criteria")}
          </button>
        )}
      </div>
      <div className="flex justify-end gap-2">
        {onClose && (
          <button
            type="button"
            className="px-4 py-2 rounded-md text-gray-400"
            onClick={onClose}
          >
            {t("cancel")}
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 rounded-md bg-primary text-white"
        >
          {t("submit_review")}
        </button>
      </div>
    </form>
  );
};

export default WriteReviewForm;
WriteReviewForm.displayName = "WriteReviewForm";
